// PromptsSection.tsx
import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { generateMainSystemAndStrategyPrompt } from "@/lib/aiPhoneAgent";

interface PromptsSectionProps {
  callDescription: string;
  callDuration: number;
  strategyType: string;
  mainSystemPrompt: string;
  strategyPrompt: string;
  onPromptsGenerated?: (mainPrompt: string, strategyPrompt: string) => void;
  onMainPromptChange: (prompt: string) => void;
  onStrategyPromptChange: (prompt: string) => void;
  onReset: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const PromptsSection = ({
  callDescription,
  callDuration,
  strategyType,
  mainSystemPrompt,
  strategyPrompt,
  onPromptsGenerated,
  onMainPromptChange,
  onStrategyPromptChange,
  onReset,
  onLoadingChange,
}: PromptsSectionProps) => {
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleGeneratePrompts = async () => {
    try {
      setError("");
      setLoading(true);
      onLoadingChange?.(true);

      const {
        mainSystemPrompt: newMainPrompt,
        strategyPrompt: newStrategyPrompt,
      } = await generateMainSystemAndStrategyPrompt(
        callDescription,
        callDuration,
        strategyType
      );

      onPromptsGenerated?.(newMainPrompt, newStrategyPrompt);
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to generate prompts. Please try again.");
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  const handleMainPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    onMainPromptChange(e.target.value);
  };

  const handleStrategyPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    onStrategyPromptChange(e.target.value);
  };

  const promptsAreEmpty = !mainSystemPrompt && !strategyPrompt;
  const canGenerate =
    !loading && callDescription && callDuration && strategyType;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex flex-wrap justify-between items-center gap-2">
          Prompts
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleGeneratePrompts}
              disabled={!canGenerate}
              variant="outline"
              size="sm"
            >
              {loading ? <Spinner /> : "Generate Prompts"}
            </Button>
            {!promptsAreEmpty && (
              <Button onClick={onReset} variant="outline" size="sm">
                Reset Prompts
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Main System Prompt</h3>
            <Textarea
              value={mainSystemPrompt}
              onChange={handleMainPromptChange}
              className="w-full h-64"
              placeholder="Generated main system prompt will appear here..."
              disabled={loading}
            />
          </div>

          <div>
            <h3 className="font-semibold mb-2">Strategy Prompt</h3>
            <Textarea
              value={strategyPrompt}
              onChange={handleStrategyPromptChange}
              className="w-full h-64"
              placeholder="Generated strategy prompt will appear here..."
              disabled={loading}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
